/**
 * 広域マスタ CRUD modal トップレベルの適用開始日 / 適用終了日 2 カラムブロック。
 *
 * design (`design/admin/occto.html` の各 -valid-from / -valid-to、 例:
 * #oem-valid-from / #bgm-valid-from / #plm-valid-from / #sm-valid-from /
 * #pm-valid-from / #tpm-valid-from) が 「1fr/1fr grid + gap=3 + 開始日は必須 +
 * 終了日はオプショナル + 「(空欄=無期限)」 hint」 で 7 タブ共通のため、
 * form ごとの手書き反復を 1 部品に集約する。
 *
 * 対象タブ: 事業者 / BG / 計画提出者 / 発電所 / 発電販売計画 / 需要調達計画 /
 * 需要抑制計画。 取引先 (`_common/TradingPartnerFormFields`) は modal トップレベルに
 * 適用期間フィールドを持たない (アカウント行ごとの期間のみ、`PeriodFields` 側の領分)。
 *
 * ラベル / hint 文言は `page:adminOcctoMaster.common.column.validFrom` /
 * `.validTo` / `.validToHint` を SoT とする (i18n 側で既に定義済み)。
 *
 * ネストされた field-array 行内の適用期間 (BG member 行等) は `PeriodFields` の
 * 領分。 本部品はトップレベル (form root の validFrom / validTo) のみを扱う。
 *
 * 実際の日付入力 UI (Flatpickr 相当 compact calendar popup) は
 * `CompactCivilDateField` に切り出して PeriodFields と共有する
 * (2026-08-05 抽出。 従来はここに DIY 実装が inline 展開されていた)。
 *
 * 適用終了日が過去日のときは欄の直下に直し方の注意文を出す (`ValidToPastWarning`)。
 * モーダル冒頭に出す「終了しています」帯は `ExpiredMasterAlert` の領分で、
 * 帯 = 状態と影響、 欄 = 直し方、 と役割を分ける。
 */
import { type Control, Controller, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FieldRequiredIndicator,
  FieldRoot,
  SimpleGrid,
  Text,
} from '@enechain/ecloud-designsystem'

import { CompactCivilDateField } from '~/components/common/CompactCivilDateField'
import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import type { CivilDate } from '~/models/civilDate'
import { FieldWarningText } from '~/pages/AdminOcctoMaster/tabs/_common/components/FieldWarningText'
import { useTodayJst } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useTodayJst'
import { isValidToPast } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type Props = {
  disabled?: boolean
  /**
   * form の Control。 個別 form の Input type は `discriminated union` (BG マスタ 等)
   * を含み FieldValues への narrowing が難しいため、 呼び出し側で
   * `Control<FormInput> as unknown as Control<FieldValues>` に cast して渡す
   * (`PlanMasterBgsSection` 等の既存 pattern と同じ)。 本部品は
   * `validFrom` / `validTo` にしかアクセスしないので型安全性への影響は
   * ローカルに閉じる。
   */
  control: Control<FieldValues>
  /**
   * htmlFor / id の prefix。 画面内で unique であれば良い
   * (例: `bgMaster`, `businessMaster`, `submitterMaster`)。
   */
  idPrefix: string
  /** validFrom の Zod / server エラーメッセージ (未定義なら error 表示なし)。 */
  validFromError?: string
  /** validTo の Zod / server エラーメッセージ。 */
  validToError?: string
}

/**
 * 適用終了日入力の DOM id。 `ExpiredMasterAlert` の「適用終了日を変更」から
 * ジャンプ先を解決するため、 id の組み立て規則をここに 1 本化する。
 */
export function topLevelValidToFieldId(idPrefix: string): string {
  return `${idPrefix}-validTo`
}

/**
 * 適用終了日が過去日のときに欄の直下へ出す注意文 (design 8080
 * `admin/occto.html` の `_occtoSyncValidToWarn` / `.form-warn-msg`)。
 *
 * **エラー (赤) にしない**: 過去日は入力として不正ではなく、 事業終了をそのまま
 * 記録した正しい状態でもある。 赤 + 保存拒否にすると「終了のまま備考だけ直す」
 * 「そのまま閉じる」が塞がるため、 保存を止めない warning に留める
 * (Des 判断 2026-08-05)。 見た目は `FieldWarningText` に集約している。
 */
const ValidToPastWarning = (): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <FieldWarningText>
      {t('page:adminOcctoMaster.common.expiredNotice.validToPastHint')}
    </FieldWarningText>
  )
}

export const TopLevelValidPeriodFields = ({
  control,
  idPrefix,
  validFromError,
  validToError,
  disabled = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const today = useTodayJst()
  const validFromLabel = t('page:adminOcctoMaster.common.column.validFrom')
  const validToLabel = t('page:adminOcctoMaster.common.column.validTo')
  return (
    <SimpleGrid gap={3} columns={2}>
      <Controller
        control={control}
        name="validFrom"
        render={({ field: { value, onChange } }) => (
          <FieldRoot invalid={validFromError !== undefined} required>
            <FieldLabel htmlFor={`${idPrefix}-validFrom`}>
              {validFromLabel}
              <FieldRequiredIndicator />
            </FieldLabel>
            <CompactCivilDateField
              disabled={disabled}
              hasError={validFromError !== undefined}
              id={`${idPrefix}-validFrom`}
              onChange={(d) => {
                if (d !== undefined) onChange(d)
              }}
              value={value as CivilDate | undefined}
            />
            {validFromError !== undefined && (
              <FieldErrorText>{validFromError}</FieldErrorText>
            )}
          </FieldRoot>
        )}
      />
      <Controller
        control={control}
        name="validTo"
        render={({ field: { value, onChange } }) => (
          <FieldRoot invalid={validToError !== undefined}>
            <FieldLabel htmlFor={topLevelValidToFieldId(idPrefix)}>
              {validToLabel}
              {/* design (`design/admin/occto.html` #oem-valid-to label 内 span):
                  「(空欄=無期限)」 は sm/400/mid-emphasis。 親 label が
                  bold/semibold のため textStyle="captionNormal" (sm/normal) で
                  weight を明示 override する必要がある (fontSize + fontWeight
                  の JSX 手書きは `ebalance/no-adhoc-typography` 違反)。
                  marginLeft=1 (4px) は design 側 literal space 幅に合わせる。 */}
              <Text
                as="span"
                textStyle="captionNormal"
                marginLeft={1}
                color="Text.midEmphasis"
              >
                {t('page:adminOcctoMaster.common.validToHint')}
              </Text>
            </FieldLabel>
            <CompactCivilDateField
              disabled={disabled}
              hasClear
              hasError={validToError !== undefined}
              id={topLevelValidToFieldId(idPrefix)}
              onChange={(d) => {
                onChange(d)
              }}
              value={value as CivilDate | undefined}
            />
            {validToError !== undefined && (
              <FieldErrorText>{validToError}</FieldErrorText>
            )}
            {isValidToPast(value as CivilDate | undefined, today) && (
              <ValidToPastWarning />
            )}
          </FieldRoot>
        )}
      />
    </SimpleGrid>
  )
}
